<script lang="ts">
  import { onMount } from 'svelte';
  import { push, location } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import DateRange from '../../DateRange.svelte';
  import Pagination from '../../Pagination.svelte';
  import Button from '../../Button.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import AsyncSearchableDropdown from '../../AsyncSearchableDropdown.svelte';
  import PaymentsService, { type OutgoingPayment } from '../../../lib/api/admin/payments.service';
  import charitiesSearchService from '../../../lib/api/admin/charities-search.service';
  import employersSearchService from '../../../lib/api/admin/employers-search.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import toast from 'svelte-french-toast';

  // Preferences manager
  const preferences = createPreferencesManager('outgoing-payments');

  // State
  let isLoading = true;
  let error = '';
  let payments: OutgoingPayment[] = [];
  let totalPages = 1;
  let totalItems = 0;

  // Metrics
  let totalPaid = 0;
  let totalToMake = 0;
  let totalAmount = 0;
  let totalL2GFee = 0;

  $: showPendingConfirmationOnly = $location.includes('confirmation=pending');
  $: displayedPayments = showPendingConfirmationOnly
    ? payments.filter(
        (payment) =>
          payment.type === 'paid' &&
          !payment.charity_confirmed_receipt &&
          !payment.charity_marked_received_at
      )
    : payments;

  // Filters - initialize from preferences
  const savedFilters = preferences.getFilters();
  let searchPhrase = savedFilters.searchPhrase || '';
  let selectedEmployer = savedFilters.selectedEmployer || '';
  let selectedCharity = savedFilters.selectedCharity || '';
  let startDate = savedFilters.startDate || '';
  let endDate = savedFilters.endDate || '';

  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 20;

  // No status filter needed - only showing completed payments

  // Search functions for async dropdowns
  async function searchCharities(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await charitiesSearchService.searchCharities(query, 10);
    } catch (err) {
      console.error('Error searching charities:', err);
      return [];
    }
  }

  async function getCharityLabel(uuid: string): Promise<string | null> {
    try {
      return await charitiesSearchService.getCharityName(uuid);
    } catch (err) {
      console.error('Error getting charity name:', err);
      return null;
    }
  }

  async function searchEmployers(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await employersSearchService.searchEmployers(query, 10);
    } catch (err) {
      console.error('Error searching employers:', err);
      return [];
    }
  }

  async function getEmployerLabel(uuid: string): Promise<string | null> {
    try {
      return await employersSearchService.getEmployerName(uuid);
    } catch (err) {
      console.error('Error getting employer name:', err);
      return null;
    }
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch {
      return 'Invalid Date';
    }
  }

  function formatPaymentId(uuid: string | null): string {
    if (!uuid) return '<span class="text-muted">—</span>';
    // Show first 8 characters of UUID for brevity
    return uuid.substring(0, 8).toUpperCase();
  }

  const tableColumns = [
    {
      key: 'employer_payment_uuid',
      label: 'Payment ID',
      width: '10%',
      render: (value: string | null) => formatPaymentId(value)
    },
    {
      key: 'transaction_id',
      label: 'Payment Reference',
      width: '12%',
      render: (value: string | null) => value || '<span class="text-muted">—</span>'
    },
    {
      key: 'charity',
      label: 'Charity',
      width: '18%',
      render: (value: any, row: OutgoingPayment) => row.charity.name
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '10%',
      render: (value: number) => formatCurrency(value)
    },
    {
      key: 'l2g_fee_percentage',
      label: 'Fee %',
      width: '8%',
      render: (value: number) => `${value.toFixed(2)}%`
    },
    {
      key: 'l2g_fee_amount',
      label: 'Transaction Fee',
      width: '10%',
      render: (value: number) => formatCurrency(value)
    },
    {
      key: 'employer_name',
      label: 'Employer',
      width: '15%',
      render: (value: string | null) => value || '<span class="text-muted">—</span>'
    },
    {
      key: 'transaction_date',
      label: 'Paid Date',
      width: '12%',
      render: (value: string | null) => formatDate(value)
    },
    {
      key: 'charity_confirmed_receipt',
      label: 'Charity receipt',
      width: '12%',
      align: 'center',
      render: (_value: boolean, row: OutgoingPayment) => {
        if (row.type !== 'paid') return '<span class="text-muted">—</span>';
        if (row.charity_confirmed_receipt || row.charity_marked_received_at) {
          return '<span class="receipt-badge confirmed">Confirmed</span>';
        }
        return '<span class="receipt-badge awaiting">Awaiting</span>';
      }
    }
  ];

  async function loadPayments() {
    try {
      isLoading = true;
      error = '';

      const filters: any = {
        page: currentPage,
        per_page: itemsPerPage,
        // "Completed" view: only payouts the charity has confirmed receipt of
        confirmed_only: true
      };

      if (searchPhrase) filters.phrase = searchPhrase;
      if (selectedEmployer) filters.employer_uuid = selectedEmployer;
      if (selectedCharity) filters.charity_uuid = selectedCharity;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await PaymentsService.getOutgoingPayments(filters);

      if (response.success && response.data) {
        // Backend now filters to only completed payments
        payments = Array.isArray(response.data) ? response.data : (response.data.items || []);
        totalPages = response.paging?.last_page || 1;
        totalItems = response.paging?.total || 0;

        // Calculate metrics (only for paid payments)
        totalPaid = payments.length;
        totalToMake = 0;
        totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
        totalL2GFee = payments.reduce((sum, p) => sum + p.l2g_fee_amount, 0);
      } else {
        error = response.message || 'Failed to load payments';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'Failed to load payments';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchPhrase = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity, startDate, endDate } });
    loadPayments();
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity, startDate, endDate } });
    loadPayments();
  }

  function handleCharityChange(value: string) {
    selectedCharity = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity, startDate, endDate } });
    loadPayments();
  }


  function handleDateRangeChange(event: CustomEvent<{ start: string; end: string }>) {
    startDate = event.detail.start;
    endDate = event.detail.end;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity, startDate, endDate } });
    loadPayments();
  }

  function handleResetFilters() {
    searchPhrase = '';
    selectedEmployer = '';
    selectedCharity = '';
    startDate = '';
    endDate = '';
    currentPage = 1;
    preferences.reset();
    loadPayments();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadPayments();
  }

  function handleItemsPerPageChange(event: CustomEvent<{ value: number }>) {
    itemsPerPage = event.detail.value;
    currentPage = 1;
    preferences.update({ pageSize: itemsPerPage, currentPage: 1 });
    loadPayments();
  }

  function handleRowClick(row: OutgoingPayment) {
    // Navigate to payment line detail view
    push(`/admin/payment-detail/${row.uuid}`);
  }

  // Watch for filter changes and save preferences
  $: if (searchPhrase !== undefined || selectedEmployer !== undefined || selectedCharity !== undefined || startDate !== undefined || endDate !== undefined) {
    preferences.updateFilters({ searchPhrase, selectedEmployer, selectedCharity, startDate, endDate });
  }

  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }

  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }

  onMount(() => {
    loadPayments();
  });
</script>

<div class="payments-list-page">
  <div class="page-header">
    <p class="subtitle">Payments to charities that have been confirmed as received</p>
  </div>

  <!-- Metrics Cards -->
  <div class="metrics-grid">
    <div class="metric-card">
      <div class="metric-label">Total Payments</div>
      <div class="metric-value">{totalItems.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Paid</div>
      <div class="metric-value">{totalPaid.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">To Make</div>
      <div class="metric-value">{totalToMake.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Total Amount</div>
      <div class="metric-value">{formatCurrency(totalAmount)}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Total Transaction Fee</div>
      <div class="metric-value">{formatCurrency(totalL2GFee)}</div>
    </div>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-row">
      <SearchInput
        placeholder="Search by payment ID, reference, transaction ID, or charity..."
        bind:value={searchPhrase}
        on:search={handleSearch}
        width="100%"
      />
      <AsyncSearchableDropdown
        placeholder="Filter by Employer"
        selectedValue={selectedEmployer}
        searchFunction={searchEmployers}
        getSelectedLabel={getEmployerLabel}
        onSelectionChange={handleEmployerChange}
      />
      <AsyncSearchableDropdown
        placeholder="Filter by Charity"
        selectedValue={selectedCharity}
        searchFunction={searchCharities}
        getSelectedLabel={getCharityLabel}
        onSelectionChange={handleCharityChange}
      />
      <DateRange
        placeholder="Paid Date Range"
        bind:startDate={startDate}
        bind:endDate={endDate}
        on:dateRangeChange={handleDateRangeChange}
      />
      {#if searchPhrase || selectedEmployer || selectedCharity || startDate || endDate}
        <Button variant="secondary" on:click={handleResetFilters}>
          Reset Filters
        </Button>
      {/if}
    </div>
  </div>

  <!-- Payments Table Section -->
  <div class="table-section">
    {#if isLoading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState
        message="Failed to load payments"
        description={error}
        onRetry={loadPayments}
      />
    {:else if payments.length === 0}
      <EmptyState
        message="No payments found"
        description="Try adjusting your search or filter criteria to see more results."
        actionLabel="Reset Filters"
        onAction={handleResetFilters}
      />
    {:else}
      <Table
        columns={tableColumns}
        data={displayedPayments}
        onRowClick={handleRowClick}
      />

      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination
            {currentPage}
            {totalPages}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .filters-section {
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-row {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .filters-row > :global(*) {
    flex: 1;
    min-width: 140px;
  }

  .filters-row > :global(*:first-child) {
    flex: 1;
    min-width: 280px;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  :global(.status-badge.status-paid) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-badge.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-badge.status-to-make) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.text-muted) {
    color: #9ca3af;
    font-style: italic;
  }

  :global(.receipt-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  :global(.receipt-badge.confirmed) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.receipt-badge.awaiting) {
    background-color: #fef3c7;
    color: #92400e;
  }
</style>

